import { APIResponse, PaginatedAPIResponse } from "../models/NetworkResponse";
import { deleteAPI, getAPI, postAPI, putAPI } from "./Networking";
import { ProjectManagementFilters } from "../models/ProjectManagement/ProjectManagementFilters";
import { ProjectManagementBasicDetails } from "../models/ProjectManagement/ProjectManagementBasicDetails";
import { ProjectDetails } from "../models/ProjectManagement/ProjectDetails";
import dayjs from "dayjs";
import { PurchaseRequestProductRequestBody } from "../models/PurchaseRequest/PurchaseRequestProductRequestBody";
import { OpsSyncProjectCategory } from "../models/ProjectManagement/OpsSyncProjectCategory";
import { OpsSyncProjectProductProgress } from "../models/ProjectManagement/OpssyncProjectProductProgress";
import { ModuleTasks } from "../models/ModuleType";
import { Department } from "../models/department/Department";
import {
  ProjectRatingRequest,
  ProjectRatingResponse,
} from "../models/OpsSync/OpsSyncFeedbackForm";
import {
  UnpaidSamplesReturnInfoUpdate,
  UnpaidSamplesReturnInfo,
} from "../models/ProjectManagement/UnpaidSample";
import { UnpaidSampleFilters } from "../models/ProjectManagement/UnpaidSampleFilters";
import {
  ProjectHandoverData,
  ProjectEmailerInfo,
  ProjectEmailerType,
} from "../models/ProjectManagement/ProjectProductsSourcingVariants";

export default class ProjectManagementAPI {
  getProjectManagementList = async (
    params: ProjectManagementFilters,
  ): Promise<PaginatedAPIResponse<ProjectManagementBasicDetails>> => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page_no)
        queryParams.append("page_no", params.page_no.toString());

      if (params.limit) queryParams.append("limit", params.limit.toString());

      if (params.sort_by) queryParams.append("sort_by", params.sort_by);

      if (params.sort_type)
        queryParams.append("sort_type", params.sort_type.toString());

      if (params.search_text && params.search_text !== "")
        queryParams.append("search_text", params.search_text);

      if (params.warehouse_id && params.warehouse_id.id !== "0")
        queryParams.append("warehouse_id", params.warehouse_id.id);

      if (params.order_type && params.order_type.length > 0) {
        let order_type = params.order_type.join(",");
        queryParams.append("order_type", order_type);
      }

      if (params.start_date) {
        const startDate = dayjs(params.start_date).toISOString();
        queryParams.append("start_date", startDate);
      }

      if (params.end_date) {
        const endDate = dayjs(params.end_date).toISOString();
        queryParams.append("end_date", endDate);
      }

      if (
        params.customer_id &&
        Array.isArray(params.customer_id) &&
        params.customer_id.length > 0
      ) {
        let customer_id = params.customer_id
          .map((customer) => customer.id)
          .join(",");
        queryParams.append("customer_id", customer_id);
      }

      if (
        params.pm_adminids &&
        Array.isArray(params.pm_adminids) &&
        params.pm_adminids.length > 0
      ) {
        let pm_adminids = params.pm_adminids
          .map((pmAdmin) => pmAdmin.id)
          .join(",");
        queryParams.append("pm_adminids", pm_adminids);
      }

      if (
        params.sales_adminids &&
        Array.isArray(params.sales_adminids) &&
        params.sales_adminids.length > 0
      ) {
        let sales_adminids = params.sales_adminids
          .map((salesAdmin) => salesAdmin.id)
          .join(",");
        queryParams.append("sales_adminids", sales_adminids);
      }

      if (params.project_status && params.project_status.length > 0) {
        queryParams.append(
          "project_status",
          params.project_status[0].id.toString(),
        );
      }

      const response = await getAPI(`/v5/projects?${queryParams.toString()}`);
      return response.result as PaginatedAPIResponse<ProjectManagementBasicDetails>;
    } catch (error) {
      throw error;
    }
  };

  getOpsSyncProjectList = async (
    params: ProjectManagementFilters,
  ): Promise<PaginatedAPIResponse<ProjectManagementBasicDetails>> => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page_no)
        queryParams.append("page_no", params.page_no.toString());

      if (params.limit) queryParams.append("limit", params.limit.toString());

      if (params.sort_by) queryParams.append("sort_by", params.sort_by);

      if (params.active) queryParams.append("active", params.active.toString());

      if (params.archived !== undefined) {
        queryParams.append("archived", params.archived.toString());
      }

      if (params.sort_type)
        queryParams.append("sort_type", params.sort_type.toString());

      if (params.search_text && params.search_text !== "")
        queryParams.append("search_text", params.search_text);

      if (params.created_date_from) {
        const startDate = dayjs(params.created_date_from).toISOString();
        queryParams.append("created_date_from", startDate);
      }

      if (params.created_date_to) {
        const endDate = dayjs(params.created_date_to).toISOString();
        queryParams.append("created_date_to", endDate);
      }

      if (params.dispatch_date_from) {
        const startDate = dayjs(params.dispatch_date_from, "YYYY-MM-DD").format(
          "YYYY-MM-DD HH:mm:ss",
        );
        queryParams.append("dispatch_date_from", startDate);
      }

      if (params.dispatch_date_to) {
        const endDate = dayjs(params.dispatch_date_to, "YYYY-MM-DD")
          .add(1, "day")
          .subtract(1, "second")
          .format("YYYY-MM-DD HH:mm:ss");
        queryParams.append("dispatch_date_to", endDate);
      }

      if (
        params.customer_id &&
        Array.isArray(params.customer_id) &&
        params.customer_id.length > 0
      ) {
        let customer_id = params.customer_id
          .map((customer) => customer.id)
          .join(",");
        queryParams.append("customer_id", customer_id);
      }

      if (
        params.pm_adminids &&
        Array.isArray(params.pm_adminids) &&
        params.pm_adminids.length > 0
      ) {
        let pm_adminids = params.pm_adminids
          .map((pmAdmin) => pmAdmin.id)
          .join(",");
        queryParams.append("pm_adminids", pm_adminids);
      }

      if (
        params.sales_adminids &&
        Array.isArray(params.sales_adminids) &&
        params.sales_adminids.length > 0
      ) {
        let sales_adminids = params.sales_adminids
          .map((salesAdmin) => salesAdmin.id)
          .join(",");
        queryParams.append("sales_adminids", sales_adminids);
      }

      if (params.project_status && params.project_status.length > 0) {
        queryParams.append(
          "project_status",
          params.project_status[0].id.toString(),
        );
      }

      if (params.project_type && params.project_type.length > 0) {
        let order_type = params.project_type
          .map((orderType) => orderType.id)
          .join(",");
        queryParams.append("order_type", order_type);
      }
      if (params.payment_status_id && params.payment_status_id.length > 0) {
        let payment_status_id = params.payment_status_id
          .map((paymentType) => paymentType.id)
          .join(",");
        queryParams.append("payment_status_id", payment_status_id);
      }

      const response = await getAPI(
        `/v5/projects/ops-sync?${queryParams.toString()}`,
      );
      return response.result as PaginatedAPIResponse<ProjectManagementBasicDetails>;
    } catch (error) {
      console.log("Error", error);
      throw error;
    }
  };

  getProjectDetails = async (projectId: number): Promise<ProjectDetails> => {
    try {
      const response = await getAPI(`/v5/projects/project_id/${projectId}`);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  saveProjectDetails = async (
    projectDetails: ProjectDetails,
  ): Promise<ProjectDetails> => {
    try {
      const response = await postAPI(`/v5/projects`, projectDetails);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  sendNpsEmail = async (
    name: string,
    email: string,
    orderId: number,
  ): Promise<APIResponse> => {
    try {
      const body = {
        acc_manager_name: name,
        acc_manager_email: email,
      };
      const response = await postAPI(
        `/v5/orders/order-id/${orderId}/nps-form/send-email`,
        body,
      );

      return response;
    } catch (error) {
      throw error;
    }
  };

  sendCsatEmail = async (orderId: number): Promise<APIResponse> => {
    try {
      const response = await postAPI(
        `/v5/orders/order-id/${orderId}/csat-form/send`,
      );

      return response;
    } catch (error) {
      throw error;
    }
  };

  getPurchaseRequestProductInsights = async (
    projectId: number,
    productIds: number[],
  ) => {
    try {
      const response = await postAPI(
        `/v5/projects/sourcing/id/${projectId}/purchase-request/insights`,
        { product_ids: productIds },
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  createPurchaseRequest = async (
    projectId: number,
    vendorId: number,
    prProducts: PurchaseRequestProductRequestBody[],
    expectedDeliveryDate: Date,
    isUrgentSourcingRequest: boolean,
    warehouseId?: string,
    otherCharges?: number | null,
  ) => {
    try {
      const response = await postAPI(
        `/v5/projects/sourcing/id/${projectId}/purchase-request`,
        {
          vendor_id: vendorId,
          pr_products: prProducts,
          expected_delivery_date: expectedDeliveryDate,
          is_urgent_sourcing_request: isUrgentSourcingRequest,
          warehouse_id: warehouseId,
          other_charges: otherCharges,
        },
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  // ops-sync
  getOpsSyncProjectCategories = async (): Promise<OpsSyncProjectCategory[]> => {
    try {
      const response = await getAPI(`/v5/projects/ops-sync-project-categories`);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getOpsSyncProjectTaskTypes = async (): Promise<ModuleTasks[]> => {
    try {
      const response = await getAPI(`/v5/projects/tasks/product-level`);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getOpsSyncProjectProductProgress = async (
    projectId: number,
  ): Promise<OpsSyncProjectProductProgress[]> => {
    try {
      const response = await getAPI(
        `/v5/projects/project-id/${projectId}/progress`,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getDepartmentList = async (): Promise<Department[]> => {
    try {
      const response = await getAPI(`/v5/departments`);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getProjectRatings = async (
    projectId: number,
  ): Promise<ProjectRatingResponse[]> => {
    try {
      const response = await getAPI(`/v6/projects/${projectId}/ratings`);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getRatingById = async (ratingId: number): Promise<ProjectRatingResponse> => {
    try {
      const response = await getAPI(`/v6/projects/rating/${ratingId}`);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  saveProjectRating = async (
    projectId: number,
    payload: ProjectRatingRequest,
  ): Promise<ProjectRatingResponse> => {
    try {
      const response = await postAPI(
        `/v6/projects/${projectId}/rating`,
        payload,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  updateProjectRating = async (
    ratingId: number,
    payload: ProjectRatingRequest,
  ): Promise<ProjectRatingResponse> => {
    try {
      const response = await putAPI(`/v6/projects/rating/${ratingId}`, payload);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  makeArchived = async (orderId: number): Promise<APIResponse> => {
    try {
      const response = await postAPI(`/v5/projects/${orderId}/archive`, {});
      return response;
    } catch (error) {
      throw error;
    }
  };

  makeUnarchived = async (orderId: number): Promise<APIResponse> => {
    try {
      const response = await putAPI(`/v5/projects/${orderId}/unarchive`, null);
      return response;
    } catch (error) {
      throw error;
    }
  };

  fetchHandoverCallInfo = async (
    projectId: number,
  ): Promise<ProjectHandoverData> => {
    try {
      const response = await getAPI(
        `/v5/projects/id/${projectId}/handover-call-info`,
        {},
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  addHandoverCallInfo = async (
    projectId: number,
    handoverCallInfo: ProjectHandoverData,
  ): Promise<number> => {
    try {
      const response = await postAPI(
        `/v5/projects/id/${projectId}/handover-call-info`,
        handoverCallInfo,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  updateHandoverCallInfo = async (
    projectId: number,
    handoverCallInfo: ProjectHandoverData,
  ): Promise<number> => {
    try {
      const response = await putAPI(
        `/v5/projects/id/${projectId}/handover-call-info`,
        handoverCallInfo,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  sendHandoverCallInfoEmail = async (
    projectId: number,
    preview_email: boolean,
  ): Promise<ProjectHandoverData> => {
    try {
      const response = await postAPI(
        `/v5/projects/id/${projectId}/handover-call-info/send-email?preview_email=${
          preview_email ? "true" : "false"
        }`,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  fetchPendingEmailerInfo = async (
    projectId: number,
    type: ProjectEmailerType,
  ): Promise<ProjectEmailerInfo> => {
    try {
      const response = await getAPI(
        `/v5/projects/id/${projectId}/emailer-info?type=${type}`,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  fetchEmailersInfo = async (
    projectId: number,
  ): Promise<ProjectEmailerInfo[]> => {
    try {
      const response = await getAPI(
        `/v5/projects/id/${projectId}/emailers-info`,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  sendPendingEmailerEmail = async (
    projectId: number,
    type: ProjectEmailerType,
    preview_email: boolean,
  ): Promise<ProjectEmailerInfo> => {
    try {
      const response = await getAPI(
        `/v5/projects/emailers/client/project_id/${projectId}?type=${type}&preview_email=${
          preview_email ? "true" : "false"
        }`,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  updateUnpaidSamplePickupDate = async (
    order_id: string,
    pickup_info: UnpaidSamplesReturnInfo,
  ): Promise<UnpaidSamplesReturnInfo> => {
    try {
      const response = await postAPI(
        `/v5/projects/unpaid-samples/return-info/${order_id}`,
        pickup_info,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getUnpaidSampleReturnListing = async (
    params: UnpaidSampleFilters,
  ): Promise<APIResponse> => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page_no)
        queryParams.append("page_no", params.page_no.toString());

      if (params.limit) queryParams.append("limit", params.limit.toString());
      if (params.sort_by) queryParams.append("sort_by", params.sort_by);
      if (params.sort_type) queryParams.append("sort_type", params.sort_type);
      if (params.order_id && params.order_id.length > 0) {
        const orderIds = params.order_id.map((item) =>
          typeof item === "number" ? item.toString() : item.id.toString(),
        );
        queryParams.append("order_id", orderIds.join(","));
      }

      if (params.admin_id && params.admin_id.length > 0)
        queryParams.append(
          "admin_id",
          params.admin_id.map((item) => item.id).join(","),
        );

      if (params.search_text)
        queryParams.append("search_text", params.search_text);

      if (params.active !== undefined)
        queryParams.append("active", params.active.toString());

      if (params.archive !== undefined)
        queryParams.append("archive", params.archive.toString());

      const response = await getAPI(
        `/v5/projects/unpaid-samples?${queryParams.toString()}`,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  updateUnpaidSamplePickupInfoUpdate = async (
    order_id: number,
    pickup_id: number,
    payload: UnpaidSamplesReturnInfoUpdate,
  ): Promise<APIResponse> => {
    try {
      const response = await putAPI(
        `/v5/projects/unpaid-samples/order-id/${order_id}/pickup-info-update/${pickup_id}`,
        payload,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  getUnpaidSampleReturnInfoById = async (
    order_id: number,
  ): Promise<UnpaidSamplesReturnInfo> => {
    try {
      const response = await getAPI(
        `/v5/projects/unpaid-samples/pickup-info/${order_id}`,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getUnpaidSampleReturnDetailsById = async (
    order_id: number,
    pickup_id: number,
  ): Promise<UnpaidSamplesReturnInfoUpdate> => {
    try {
      const response = await getAPI(
        `/v5/projects/unpaid-samples/order-id/${order_id}/pickup-info-details/${pickup_id}`,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  deleteUnpaidSampleProductImage = async (
    pickup_id: number,
    image_url: string,
    id: number,
  ): Promise<APIResponse> => {
    try {
      const response = await deleteAPI(
        `/v5/projects/unpaid-samples/product-images/${pickup_id}`,
        {
          image_url: image_url,
          id: id,
        },
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  updateBackfillAllowed = async (
    projectId: number,
    backfillAllowed: boolean,
  ) => {
    try {
      const response = await putAPI(
        `/v5/projects/inventory-ops/id/${projectId}/backfill-allowed/${backfillAllowed}`,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };
}
